import React, { Children, cloneElement, isValidElement, useEffect, useId, useRef } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { Dialog } from 'primereact/dialog'
import { WingsButton } from '../buttons'
import { WingsEyebrow, WingsHeading, WingsText } from '../typography'
import './WingsModal.css'

/*
 * Reference-counted page scroll lock.
 *
 * PrimeReact's `blockScroll` prop only toggles a class on <body>, but this
 * site's base layout CSS explicitly sets `html { overflow-y: scroll; }`,
 * which makes <html> — not <body> — the element that actually scrolls the
 * page. Locking body alone has no effect in that case, so we directly lock
 * <html> via inline style (which always wins over stylesheet rules). The
 * counter allows multiple WingsModals to be open at once without one
 * closing and prematurely unlocking scroll for the other.
 */
let scrollLockCount = 0
let previousHtmlOverflow = null
let previousHtmlPaddingRight = null

const lockPageScroll = () => {
  if (scrollLockCount === 0) {
    const htmlElement = document.documentElement
    const scrollbarWidth = Math.max(0, window.innerWidth - htmlElement.clientWidth)
    const currentPaddingRight = parseFloat(window.getComputedStyle(htmlElement).paddingRight) || 0

    previousHtmlOverflow = htmlElement.style.overflow
    previousHtmlPaddingRight = htmlElement.style.paddingRight
    htmlElement.classList.add('w-modal-scroll-locked')
    htmlElement.style.setProperty('--w-modal-scrollbar-compensation', `${scrollbarWidth}px`)
    htmlElement.style.overflow = 'hidden'

    // Keep layout width stable when the viewport scrollbar disappears.
    if (scrollbarWidth > 0) {
      htmlElement.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`
    }
  }
  scrollLockCount += 1
}

const unlockPageScroll = () => {
  scrollLockCount = Math.max(0, scrollLockCount - 1)
  if (scrollLockCount === 0) {
    const htmlElement = document.documentElement
    htmlElement.style.overflow = previousHtmlOverflow || ''
    htmlElement.style.paddingRight = previousHtmlPaddingRight || ''
    htmlElement.style.removeProperty('--w-modal-scrollbar-compensation')
    htmlElement.classList.remove('w-modal-scroll-locked')
    previousHtmlOverflow = null
    previousHtmlPaddingRight = null
  }
}

const modalSizeMap = {
  sm: {
    width: 300,
    padding: 17,
    titleLevel: 6,
    actionSize: 'mini',
    headerAlign: 'left',
  },
  md: {
    width: 500,
    padding: 34,
    titleLevel: 5,
    actionSize: 'mini',
    headerAlign: 'left',
  },
  lg: {
    width: 800,
    padding: 34,
    titleLevel: 5,
    actionSize: 'small',
    headerAlign: 'left',
  },
}

const displayEligibleSizes = new Set(['md', 'lg'])

const MODAL_SLOT_NAME = {
  header: 'WingsModalHeader',
  body: 'WingsModalBody',
  footer: 'WingsModalFooter',
  actions: 'WingsModalActions',
}

const getSlotName = (child) => {
  if (!isValidElement(child)) return null
  return child.type?.slotName || child.type?.displayName || null
}

// Resolves a text prop's active-language value from a base value plus an
// optional `*Es` companion prop, so consumers never need to know the current
// locale or wrap copy in translation components themselves.
const pickLocalizedText = (locale, base, esVariant) => {
  if (esVariant != null && typeof locale === 'string' && locale.toLowerCase().startsWith('es')) {
    return esVariant
  }
  return base
}

const normalizeLooseChildren = (children) => Children.toArray(children).map((child, index) => {
  if (typeof child === 'string' || typeof child === 'number') {
    return (
      <WingsText key={`w-modal-text-${index}`}>
        {child}
      </WingsText>
    )
  }
  return child
})

const WingsModalHeader = ({ children, align = 'left', className = '' }) => (
  <div
    className={[
      'w-modal-header',
      align === 'center' ? 'w-modal-header-center' : 'w-modal-header-left',
      className,
    ].filter(Boolean).join(' ')}
  >
    {children}
  </div>
)

WingsModalHeader.slotName = MODAL_SLOT_NAME.header
WingsModalHeader.displayName = MODAL_SLOT_NAME.header

WingsModalHeader.propTypes = {
  children: PropTypes.node.isRequired,
  align: PropTypes.oneOf(['left', 'center']),
  className: PropTypes.string,
}

const WingsModalBody = ({ children, align = 'left', className = '' }) => (
  <div
    className={[
      'w-modal-body',
      align === 'center' ? 'w-modal-body-center' : 'w-modal-body-left',
      className,
    ].filter(Boolean).join(' ')}
  >
    {children}
  </div>
)

WingsModalBody.slotName = MODAL_SLOT_NAME.body
WingsModalBody.displayName = MODAL_SLOT_NAME.body

WingsModalBody.propTypes = {
  children: PropTypes.node.isRequired,
  align: PropTypes.oneOf(['left', 'center']),
  className: PropTypes.string,
}

const WingsModalActions = ({ children, className = '' }) => (
  <div className={['w-modal-footer-actions', className].filter(Boolean).join(' ')}>{children}</div>
)

WingsModalActions.slotName = MODAL_SLOT_NAME.actions
WingsModalActions.displayName = MODAL_SLOT_NAME.actions

WingsModalActions.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
}

const WingsModalFooter = ({ children, className = '' }) => (
  <div className={['w-modal-footer', className].filter(Boolean).join(' ')}>{children}</div>
)

WingsModalFooter.slotName = MODAL_SLOT_NAME.footer
WingsModalFooter.displayName = MODAL_SLOT_NAME.footer

WingsModalFooter.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
}

/**
 * WingsModal Component
 *
 * PrimeReact-powered modal wrapper following Wingspans anatomy and sizing
 * rules. Supports both prop-driven usage and child composition via
 * `WingsModal.Header`, `WingsModal.Body`, `WingsModal.Footer`, and
 * `WingsModal.Actions`.
 *
 * Size rules:
 * - `lg`: 800px width, 34px padding, H5 title by default (H3 in display mode), centered header, small action buttons
 * - `md`: 500px width, 34px padding, left header, mini action buttons
 * - `sm`: 300px width, 17px padding, left header, tiny action buttons
 *
 * System rules enforced by this component:
 * 1. Padding scales by size: 34px for `lg`/`md`, 17px for `sm`.
 * 2. Header hierarchy scales: H3 for display mode (`md`/`lg`), H5 for standard `md`/`lg`, H6 for `sm`.
 * 3. Footer actions scale by size: small (`lg`), mini (`md`), and tiny (`sm`).
 * 4. Header alignment: centered for `lg`, left for `md`/`sm`.
 * 5. Footer layout: optional back action on the left, action group on the
 *    right, secondary action before primary action.
 * 6. Primary action supports disabled state styling and behavior.
 * 7. Desktop widths are fixed values (800/500/300), not max-width based.
 *
 * Props reference:
 * - isOpen: boolean to control visibility.
 * - onClose: callback fired when modal should close.
 * - size: `sm` | `md` | `lg`.
 * - display: `standard` | `display` | boolean (`false` => standard, `true` => display).
 * - className: extra class for dialog root.
 * - contentClassName: extra class for dialog content wrapper.
 * - children: modal content, including optional slot composition.
 * - titleEyebrow: optional content shown above title in default header.
 * - titleEyebrowEs: Spanish variant of `titleEyebrow`, used automatically when the active locale is Spanish.
 * - eyebrow: deprecated alias for `titleEyebrow`.
 * - eyebrowEs: deprecated alias for `titleEyebrowEs`.
 * - title: optional default header title content.
 * - titleEs: Spanish variant of `title`, used automatically when the active locale is Spanish.
 * - titleLevel: optional override `1..6`.
 * - header: custom header node (replaces default eyebrow/title header).
 * - headerAlign: `left` | `center`.
 * - align: `standard` | `centered`.
 * - showCloseButton: toggles top-right close button.
 * - closeLabel: accessible label for close button.
 * - closeLabelEs: Spanish variant of `closeLabel`.
 * - dismissOnMask: allow backdrop click dismiss.
 * - dismissOnEscape: allow Escape key dismiss.
 * - blockScroll: lock page scroll while modal is open.
 * - backAction: custom back action node.
 * - backActionLabel: label for generated back button.
 * - backActionLabelEs: Spanish variant of `backActionLabel`.
 * - onBackAction: click handler for generated back button.
 * - secondaryAction: custom secondary action node.
 * - secondaryActionLabel: label for generated secondary button.
 * - secondaryActionLabelEs: Spanish variant of `secondaryActionLabel`.
 * - onSecondaryAction: click handler for generated secondary button.
 * - secondaryActionDisabled: disable generated secondary button.
 * - primaryAction: custom primary action node.
 * - primaryActionLabel: label for generated primary button.
 * - primaryActionLabelEs: Spanish variant of `primaryActionLabel`.
 * - onPrimaryAction: click handler for generated primary button.
 * - primaryActionDisabled: disable generated primary button.
 *
 * Localized text props: any `*Es` prop is shown automatically instead of its
 * base prop when the app's active react-intl locale is Spanish (`es-ES`).
 * There is no need to reference any modal class names to make this happen.
 * - actionSize: `large` | `medium` | `small` | `mini` | `tiny`.
 * - footer: custom footer node (replaces generated footer/actions).
 * - appendTo: portal target for PrimeReact Dialog.
 * - style: inline style overrides for dialog root.
 * - contentStyle: inline style overrides for content wrapper.
 * - transitionOptions: PrimeReact transition lifecycle options.
 * - ...dialogProps: additional props forwarded to PrimeReact Dialog.
 */
const WingsModal = ({
  isOpen,
  onClose,
  size = 'md',
  display = 'standard',
  className = '',
  contentClassName = '',
  children,
  titleEyebrow,
  titleEyebrowEs,
  eyebrow,
  eyebrowEs,
  title,
  titleEs,
  titleLevel,
  header,
  headerAlign,
  align = 'standard',
  bodyAlign,
  showCloseButton = true,
  closeLabel = 'Close modal',
  closeLabelEs,
  dismissOnMask = true,
  dismissOnEscape = true,
  blockScroll = true,
  backAction,
  backActionLabel,
  backActionLabelEs,
  onBackAction,
  secondaryAction,
  secondaryActionLabel,
  secondaryActionLabelEs,
  onSecondaryAction,
  secondaryActionDisabled = false,
  primaryAction,
  primaryActionLabel,
  primaryActionLabelEs,
  onPrimaryAction,
  primaryActionDisabled = false,
  buttonPosition,
  actionSize,
  footer,
  appendTo,
  style: dialogStyle,
  contentStyle: dialogContentStyle,
  transitionOptions: dialogTransitionOptions,
  ...dialogProps
}) => {
  const { locale } = useIntl()
  const titleId = useId()
  const hasLockedScrollRef = useRef(false)
  const hasWarnedDeprecatedEyebrowRef = useRef(false)
  const hasWarnedDeprecatedBodyAlignRef = useRef(false)
  const hasWarnedDeprecatedButtonPositionRef = useRef(false)

  const lockModalScroll = () => {
    if (!hasLockedScrollRef.current) {
      lockPageScroll()
      hasLockedScrollRef.current = true
    }
  }

  const unlockModalScroll = () => {
    if (hasLockedScrollRef.current) {
      unlockPageScroll()
      hasLockedScrollRef.current = false
    }
  }

  const resolvedSize = modalSizeMap[size] || modalSizeMap.md
  const normalizedDisplay = display === true
    ? 'display'
    : display === false || display == null
      ? 'standard'
      : display
  const isDisplayEnabled = normalizedDisplay === 'display' && displayEligibleSizes.has(size)
  const resolvedHeaderAlign = headerAlign || (isDisplayEnabled ? 'center' : resolvedSize.headerAlign)
  const legacyAlign =
    (bodyAlign === 'center' || buttonPosition === 'center')
      ? 'centered'
      : (bodyAlign === 'left' || buttonPosition === 'right')
        ? 'standard'
        : null
  const resolvedAlign = align || legacyAlign || 'standard'
  const resolvedBodyAlign = resolvedAlign === 'centered' ? 'center' : 'left'
  const resolvedTitleLevel = titleLevel || (isDisplayEnabled ? 3 : resolvedSize.titleLevel)
  const resolvedTitleEyebrow = pickLocalizedText(
    locale,
    titleEyebrow ?? eyebrow,
    titleEyebrowEs ?? eyebrowEs,
  )
  const resolvedTitle = pickLocalizedText(locale, title, titleEs)
  const resolvedCloseLabel = pickLocalizedText(locale, closeLabel, closeLabelEs)
  const resolvedBackActionLabel = pickLocalizedText(locale, backActionLabel, backActionLabelEs)
  const resolvedSecondaryActionLabel = pickLocalizedText(locale, secondaryActionLabel, secondaryActionLabelEs)
  const resolvedPrimaryActionLabel = pickLocalizedText(locale, primaryActionLabel, primaryActionLabelEs)
  const resolvedFooterButtonsClass = resolvedAlign === 'centered'
    ? 'w-modal-footer-buttons-center'
    : 'w-modal-footer-buttons-right'
  const resolvedActionSize = actionSize || resolvedSize.actionSize
  const resolvedDialogStyle = {
    width: `${resolvedSize.width}px`,
    ...dialogStyle,
  }
  const resolvedDialogContentStyle = {
    maxHeight: 'var(--w-modal-content-max-height, calc(100vh - 2.5rem))',
    overflow: 'hidden',
    ...dialogContentStyle,
  }
  const resolvedTransitionOptions = {
    ...dialogTransitionOptions,
    onExited: (...args) => {
      unlockModalScroll()
      dialogTransitionOptions?.onExited?.(...args)
    },
  }

  useEffect(() => {
    if (!blockScroll) {
      unlockModalScroll()
      return undefined
    }

    if (isOpen) {
      lockModalScroll()
    }

    return undefined
  }, [isOpen, blockScroll])

  useEffect(() => () => {
    unlockModalScroll()
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    if (eyebrow == null) return
    if (hasWarnedDeprecatedEyebrowRef.current) return

    // Keep backward compatibility while guiding consumers to the new prop.
    console.warn('[WingsModal] `eyebrow` is deprecated. Use `titleEyebrow` instead.')
    hasWarnedDeprecatedEyebrowRef.current = true
  }, [eyebrow])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    if (bodyAlign == null) return
    if (hasWarnedDeprecatedBodyAlignRef.current) return

    console.warn('[WingsModal] `bodyAlign` is deprecated. Use `align` ("standard" | "centered") instead.')
    hasWarnedDeprecatedBodyAlignRef.current = true
  }, [bodyAlign])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    if (buttonPosition == null) return
    if (hasWarnedDeprecatedButtonPositionRef.current) return

    console.warn('[WingsModal] `buttonPosition` is deprecated. Use `align` ("standard" | "centered") instead.')
    hasWarnedDeprecatedButtonPositionRef.current = true
  }, [buttonPosition])

  const slots = {
    header: null,
    body: null,
    footer: null,
  }
  const looseChildren = []

  Children.forEach(children, (child) => {
    const slotName = getSlotName(child)

    if (slotName === MODAL_SLOT_NAME.header) {
      slots.header = child
      return
    }
    if (slotName === MODAL_SLOT_NAME.body) {
      slots.body = child
      return
    }
    if (slotName === MODAL_SLOT_NAME.footer) {
      slots.footer = child
      return
    }

    looseChildren.push(child)
  })

  const renderDefaultHeader = () => {
    if (!resolvedTitleEyebrow && !resolvedTitle && !header) return null

    if (header) {
      return (
        <WingsModalHeader align={resolvedHeaderAlign}>
          {header}
        </WingsModalHeader>
      )
    }

    return (
      <WingsModalHeader align={resolvedHeaderAlign}>
        {resolvedTitleEyebrow && (
          <WingsEyebrow as="p" color="blue" className="w-modal-eyebrow">
            {resolvedTitleEyebrow}
          </WingsEyebrow>
        )}
        {resolvedTitle && (
          <WingsHeading id={titleId} level={resolvedTitleLevel} className="w-modal-title">
            {resolvedTitle}
          </WingsHeading>
        )}
      </WingsModalHeader>
    )
  }

  const renderHeader = () => {
    if (!slots.header) return renderDefaultHeader()

    return cloneElement(slots.header, {
      align: slots.header.props.align || resolvedHeaderAlign,
      className: ['w-modal-header-composed', slots.header.props.className].filter(Boolean).join(' '),
    })
  }

  const renderBody = () => {
    if (slots.body) {
      return cloneElement(slots.body, {
        align: slots.body.props.align || resolvedBodyAlign,
        className: ['w-modal-body-composed', slots.body.props.className].filter(Boolean).join(' '),
      })
    }
    if (!looseChildren.length) return null
    return <WingsModalBody align={resolvedBodyAlign}>{normalizeLooseChildren(looseChildren)}</WingsModalBody>
  }

  const renderDefaultFooter = () => {
    const hasBack = backAction || resolvedBackActionLabel
    const hasSecondary = secondaryAction || resolvedSecondaryActionLabel
    const hasPrimary = primaryAction || resolvedPrimaryActionLabel
    if (footer) return footer
    if (!hasBack && !hasSecondary && !hasPrimary) return null

    return (
      <WingsModalFooter className={resolvedFooterButtonsClass}>
        <div className="w-modal-footer-back">
          {backAction || (hasBack ? (
            <WingsButton
              variant="off-site"
              size={resolvedActionSize}
              inline
              iconName="chevron-left"
              onClick={onBackAction}
            >
              {resolvedBackActionLabel}
            </WingsButton>
          ) : null)}
        </div>

        <WingsModalActions>
          {secondaryAction || (hasSecondary ? (
            <WingsButton
              variant="secondary"
              size={resolvedActionSize}
              onClick={onSecondaryAction}
              disabled={secondaryActionDisabled}
            >
              {resolvedSecondaryActionLabel}
            </WingsButton>
          ) : null)}

          {primaryAction || (hasPrimary ? (
            <WingsButton
              variant="primary"
              size={resolvedActionSize}
              onClick={onPrimaryAction}
              disabled={primaryActionDisabled}
            >
              {resolvedPrimaryActionLabel}
            </WingsButton>
          ) : null)}
        </WingsModalActions>
      </WingsModalFooter>
    )
  }

  const renderFooter = () => {
    if (slots.footer) {
      return cloneElement(slots.footer, {
        className: [
          slots.footer.props.className,
          resolvedFooterButtonsClass,
        ].filter(Boolean).join(' '),
      })
    }
    return renderDefaultFooter()
  }

  return (
    <Dialog
      visible={isOpen}
      onHide={onClose}
      modal
      draggable={false}
      resizable={false}
      dismissableMask={dismissOnMask}
      closeOnEscape={dismissOnEscape}
      blockScroll={blockScroll}
      showHeader={false}
      appendTo={appendTo}
      className={[
        'w-modal',
        `w-modal-size-${size}`,
        `w-modal-display-${isDisplayEnabled ? 'display' : 'standard'}`,
        className,
      ].filter(Boolean).join(' ')}
      maskClassName="w-modal-mask"
      contentClassName={['w-modal-dialog-content', contentClassName].filter(Boolean).join(' ')}
      style={resolvedDialogStyle}
      contentStyle={resolvedDialogContentStyle}
      transitionOptions={resolvedTransitionOptions}
      aria-labelledby={resolvedTitle ? titleId : undefined}
      {...dialogProps}
    >
      <div
        className={[
          'w-modal-content',
          'w-modal-content-scroll-inside',
        ].filter(Boolean).join(' ')}
        style={{ '--w-modal-padding': `${resolvedSize.padding}px` }}
      >
        {showCloseButton && (
          <button
            type="button"
            className="w-modal-close"
            onClick={onClose}
            aria-label={resolvedCloseLabel}
          >
            ✕
          </button>
        )}

        {renderHeader()}
        {renderBody()}
        {renderFooter()}
      </div>
    </Dialog>
  )
}

WingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  display: PropTypes.oneOfType([
    PropTypes.oneOf(['standard', 'display']),
    PropTypes.bool,
  ]),
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  children: PropTypes.node,
  titleEyebrow: PropTypes.node,
  titleEyebrowEs: PropTypes.node,
  eyebrow: PropTypes.node,
  eyebrowEs: PropTypes.node,
  title: PropTypes.node,
  titleEs: PropTypes.node,
  titleLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  header: PropTypes.node,
  headerAlign: PropTypes.oneOf(['left', 'center']),
  align: PropTypes.oneOf(['standard', 'centered']),
  bodyAlign: PropTypes.oneOf(['left', 'center']),
  showCloseButton: PropTypes.bool,
  closeLabel: PropTypes.string,
  closeLabelEs: PropTypes.string,
  dismissOnMask: PropTypes.bool,
  dismissOnEscape: PropTypes.bool,
  blockScroll: PropTypes.bool,
  backAction: PropTypes.node,
  backActionLabel: PropTypes.node,
  backActionLabelEs: PropTypes.node,
  onBackAction: PropTypes.func,
  secondaryAction: PropTypes.node,
  secondaryActionLabel: PropTypes.node,
  secondaryActionLabelEs: PropTypes.node,
  onSecondaryAction: PropTypes.func,
  secondaryActionDisabled: PropTypes.bool,
  primaryAction: PropTypes.node,
  primaryActionLabel: PropTypes.node,
  primaryActionLabelEs: PropTypes.node,
  onPrimaryAction: PropTypes.func,
  primaryActionDisabled: PropTypes.bool,
  buttonPosition: PropTypes.oneOf(['right', 'center']),
  actionSize: PropTypes.oneOf(['large', 'medium', 'small', 'mini', 'tiny']),
  footer: PropTypes.node,
  appendTo: PropTypes.any,
  style: PropTypes.object,
  contentStyle: PropTypes.object,
  transitionOptions: PropTypes.object,
}

WingsModal.Header = WingsModalHeader
WingsModal.Body = WingsModalBody
WingsModal.Footer = WingsModalFooter
WingsModal.Actions = WingsModalActions

export default WingsModal
